import type { BasicFetch, RequestInit } from '../types/basicFetch';
import { makeJwtCache } from './cachedJwt';
import { refreshTokenRequestBody, refreshTokenRequestContentType } from '../util/oauth2Request';
import { fetchOrThrow } from '../io/fetchOrThrow';
import { invariant } from '../util/invariant';
import { getOpenIdConnectProviderUrl } from '../util/cognitoRequest';
import type { ServiceDocument } from '../dto/ServiceDocumentDtos';
import { MIMIR_JWT_HTTP_HEADER } from '../util/constants';
import { wellKnownOidcConfigUrl } from '../util/oidcRequest';
import type { AdditionalParams, ApiCallResult } from '../util/apiClient';
import type { Logger } from '../types/logger';

export interface AuthHeaderSource {
  cachedAuthHeaders(now: number): [string, string][] | null;
  newAuthHeaders(now: number): Promise<[string, string][]>;
}

class OAuth2AuthHeaderSource implements AuthHeaderSource {
  private jwtCache = makeJwtCache();
  constructor(private logger: Logger, private fetch: BasicFetch, private refreshToken: string, private tokenEndpoint: string, private clientId: string) {
  }

  cachedAuthHeaders(now: number): [string, string][] | null {
    const jwt = this.jwtCache.getNonexpired(now);
    if (jwt == null) {
      return null;
    }
    return [[MIMIR_JWT_HTTP_HEADER, jwt]];
  }

  async newAuthHeaders(now: number): Promise<[string, string][]> {
    const te = new TextEncoder();
    const tokenResponse = await fetchOrThrow(this.logger, this.fetch, this.tokenEndpoint, {
      method: 'POST',
      headers: [['Content-Type', refreshTokenRequestContentType]],
      body: te.encode(refreshTokenRequestBody(this.refreshToken, this.clientId))
    });
    const tokens: unknown = await tokenResponse.json();
    const jwt = (tokens as any)?.id_token;
    invariant(typeof jwt === 'string', 'Expected id_token string in token response');
    this.jwtCache.putFresh(now, jwt);
    return [[MIMIR_JWT_HTTP_HEADER, jwt]];
  }
}

export class StaticAuthHeaderSource implements AuthHeaderSource {
  constructor(private headers: [string, string][]) {
  }

  cachedAuthHeaders(): [string, string][] {
    return this.headers;
  }

  newAuthHeaders(): Promise<[string, string][]> {
    return Promise.resolve(this.headers);
  }
}

export async function fetchCognitoJwtSource(logger: Logger, fetch: BasicFetch, sdoc: ServiceDocument, refreshToken: string) {
  const oidcDiscovery = wellKnownOidcConfigUrl(getOpenIdConnectProviderUrl(sdoc.clientConfig.authRegion, sdoc.clientConfig.userPoolId, sdoc.clientConfig.cognitoEndpoint));
  const discoveryResponse = await fetchOrThrow(logger, fetch, oidcDiscovery, {});
  const discovery = await discoveryResponse.json();
  const token_endpoint = discovery?.token_endpoint;
  invariant(typeof token_endpoint === 'string', 'Expected token_endpoint string from OIDC discovery');
  return new OAuth2AuthHeaderSource(logger, fetch, refreshToken, token_endpoint, sdoc.clientConfig.appClientId);
}

export async function callMimirWithFetch(logger: Logger, fetch: BasicFetch, authSource: AuthHeaderSource, url: string, method: string, additionalParams: AdditionalParams | undefined, bodyUnk: any): Promise<ApiCallResult> {
  const req: RequestInit = { method };
  if (bodyUnk != null) {
    invariant(!(bodyUnk instanceof Uint8Array), 'Did not expect Uint8Array body');
    invariant(typeof bodyUnk === 'object', 'Expected JSON object body');
    const te = new TextEncoder();
    req.body = te.encode(JSON.stringify(bodyUnk));
  }

  invariant(!additionalParams?.headers, 'additionalParams.headers not implemented');

  const queryParams = additionalParams?.queryParams;
  if (queryParams && Object.keys(queryParams).length > 0) {
    url += '?' + new URLSearchParams(queryParams);
  }

  const now = Date.now();
  const authHeaders = authSource.cachedAuthHeaders(now) ?? await authSource.newAuthHeaders(now);
  const extraHeaders: [string, string][] = [];
  if (req.body) {
    extraHeaders.push(['Content-Type', 'application/json; charset=utf-8']);
  }

  req.headers = [...authHeaders, ...extraHeaders];
  const response = await fetchOrThrow(logger, fetch, url, req);
  let data: unknown = null;
  if (response.status !== 204 && (response.headers.get('content-length') ?? '0') !== '0') {
    data = await response.json();
  }
  return {
    status: response.status,
    data
  };
}
